import React, { useState } from 'react';
import { Alert, Box, LinearProgress } from '@mui/material';
import _ from 'lodash';

import { DeedFileReader } from 'containers/DeedFileReader';
import { useEmployeesLazyQuery } from 'generated/graphql';
import { Employee } from 'utils/useFetchEmployees';

export type MembersUploadProps = {
  onChange: (data: Employee[], invalidEmails: string[]) => void;
};

export const MembersUpload = ({ onChange }: MembersUploadProps): JSX.Element => {
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  const [fetchEmployees, { loading, error }] = useEmployeesLazyQuery({
    onCompleted: (result) => {
      const users = result?.employees?.users || [];
      const uploadedUserEmails = users.map((u) => u.email);
      const invalidEmails = _.difference<string>(parsedEmails, uploadedUserEmails);

      onChange(result.employees.users, invalidEmails);
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return (
      <Box mt={1} mb={2}>
        <LinearProgress />
      </Box>
    );
  }

  const isEmail = /^\S+@\S+\.\S+$/;
  return (
    <>
      {error && <Alert severity="error">An error occurred while uploading employees</Alert>}
      <Box mt={1} mb={2}>
        <DeedFileReader
          label="Upload"
          description="Drag and drop or upload a .csv, .xls, or .xlsx file containing employee emails"
          onChange={(rows: unknown[]) => {
            // Find anything looking like an email within the uploaded file
            const emails = _.uniq(
              ((rows as string[]) || [])
                .flat()
                .map((value) => value?.toString()?.trim()?.toLowerCase())
                .filter((cell) => isEmail.test(cell))
            );

            setParsedEmails(emails);

            if (emails?.length > 0) {
              fetchEmployees({
                variables: {
                  emails: emails.join(','),
                  take: 5000,
                },
              });
            }
          }}
        />
      </Box>
    </>
  );
};
